import { Alert, AlertTitle, Box, Button, Chip, Paper, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Bills from earlier cycles that were never settled. They used to drop off the
// screen the moment a newer bill of the same type appeared, so a tenant could
// owe August's rent without anything on screen saying so.

const PAY_TYPE = {
  electricity: "kelectric",
  ssgc: "ssgc",
  water: "water",
  maintainance: "maintainance",
  trash: "trash",
};

// Paid online, on the bill's own amount plus the late fee it has earned.
const ONLINE_TYPES = ["rent", "fixedCharges", "security"];

const rs = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;

export default function PreviousUnpaidBills({ items = [], total = 0, onPay, onPayOnline, payingId }) {
  if (!items.length) return null;

  return (
    <Paper
      elevation={0}
      sx={{ p: 3, mb: 4, borderRadius: 3, border: "2px solid #f59e0b", bgcolor: "#fffbeb" }}
    >
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        sx={{ mb: 2, bgcolor: "transparent", p: 0, "& .MuiAlert-message": { width: "100%" } }}
      >
        <AlertTitle sx={{ fontWeight: 800 }}>
          Previous unpaid bills — {rs(total)} pending
        </AlertTitle>
        These are from earlier months and are still due. You cannot move out until
        every bill, rent and late fee is cleared.
      </Alert>

      {items.map((row) => {
        const payType = PAY_TYPE[row.key];
        return (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              py: 1.25,
              borderTop: "1px solid #fde68a",
            }}
          >
            <Box>
              <Typography fontWeight={700}>
                {row.label} {row.cycleKey ? `· ${row.cycleKey}` : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Due {row.dueDate || "—"}
                {row.approvalStatus === "rejected" ? " · payment was rejected" : ""}
              </Typography>
              {row.daysOverdue > 0 && (
                <Chip
                  size="small"
                  color="error"
                  sx={{ mt: 0.5 }}
                  label={`${row.daysOverdue} day${row.daysOverdue === 1 ? "" : "s"} late${
                    row.penalty > 0 ? ` · late fee ${rs(row.penalty)}` : ""
                  }`}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontWeight={800}>{rs(row.payable)}</Typography>
              {payType ? (
                <Button size="small" variant="contained" onClick={() => onPay?.(payType, row.bill)}>
                  Pay
                </Button>
              ) : ONLINE_TYPES.includes(row.key) ? (
                <Button
                  size="small"
                  variant="contained"
                  disabled={payingId === row.bill?._id}
                  onClick={() => onPayOnline?.(row.key, row.bill)}
                >
                  Pay online
                </Button>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
}
